import { useInfiniteQuery } from '@tanstack/react-query';
import ms from 'ms';

import { toast } from '@hivebrite/design-system/Toast';

import { getMentoringCustomAttributes } from 'hb-react/frontoffice/apps/Mentoring/requests';
import {
  type CustomAttributeConfig,
  type CustomAttributeType,
  type Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { getProgramTopicId } from 'hb-react/frontoffice/apps/Mentoring/utils';
import { QUERY_KEYS } from 'hb-react/shared/apps/Mentoring/constants';

interface GetMenteesCustomAttributesParams {
  program: Pick<Program, 'id' | 'parent_id' | 'parent_type'>;
  queryConfig: Partial<CustomAttributeConfig>;
  requestedFields?: Array<keyof CustomAttributeType>;
}

export const useGetMenteesCustomAttributes = ({
  program,
  queryConfig,
  requestedFields = [],
}: GetMenteesCustomAttributesParams) => {
  const {
    id: programId,
    parent_id: parentId,
    parent_type: parentType,
  } = program;

  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.menteesCustomAttributes,
      programId,
      queryConfig,
      requestedFields,
    ],
    queryFn: ({ signal, pageParam }) =>
      getMentoringCustomAttributes({
        routeApi:
          Routes.mentorings_program_mentee_customizable_attributes_path(
            programId,
          ),
        signal,
        afterCursor: pageParam === '0' ? undefined : pageParam,
        queryConfig,
        topicId: getProgramTopicId({ parentId, parentType }),
        requestedFields,
      }).catch((error) => {
        if (error.response?.status === 500) {
          toast.error(__('Sorry, an error occurred'));
        }
        throw error;
      }),
    initialPageParam: '0',
    getNextPageParam: ({
      has_next_page: hasNextPage,
      end_cursor: endCursor,
    }) => (hasNextPage ? endCursor : undefined),
    staleTime: ms('5m'),
    enabled: !!programId,
  });
};
